import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useKeycloakToken } from 'components/common/useKeycloak';
import {
  buttonClasses,
  formClasses,
  toggleClasses,
} from 'components/common/tailwindClassnames';
import { createSource, getSourcesForRoute } from 'api/sources/sourcesApi';
import ModalShell from 'components/common/ModalShell';
import AddDeviceModal from './AddDeviceModal';
import DeviceRow from './DeviceRow';
import {
  SourceFilterDraft,
  SourceFilterMode,
  SourceSummary,
} from './sourceFilterTypes';

// The source-filter editor (GUNDI-5178).
//
// The rule stores only EXCEPTIONS to a default — "everything reaches" unless the
// mode says otherwise — so the design keeps unexceptional rows neutral: no color,
// no state chip, just a Block/Allow action. Only the rows the rule actually names
// get color and a section of their own, because those are the only rows where the
// rule says anything.
//
// The provider's devices are browsable a page at a time. What is avoided is holding
// the whole catalogue in memory: a provider can have far more devices than a rule's
// cap, so the list pages and the search narrows it server-side.

type SourceFiltersFormProps = {
  providerId: string;
  destinationName: string;
  value: SourceFilterDraft;
  onChange: (next: SourceFilterDraft) => void;
};

type ListTab = 'all' | 'marked' | 'unmarked';

const SEARCH_DEBOUNCE_MS = 300;

const segmentActive = `${toggleClasses.option} ${toggleClasses.active}`;
const segmentInactive = `${toggleClasses.option} ${toggleClasses.inactive}`;

const tabBase =
  'px-2.5 py-1 text-xs font-semibold rounded cursor-pointer transition-colors duration-200';

const SourceFiltersForm = ({
  providerId,
  destinationName,
  value,
  onChange,
}: SourceFiltersFormProps) => {
  const { t } = useTranslation();
  const [token] = useKeycloakToken();
  const [search, setSearch] = useState('');
  const [devices, setDevices] = useState<SourceSummary[]>([]);
  const [total, setTotal] = useState(0);
  // The provider's device count irrespective of the search term — the summary and
  // the tab counts describe the whole rule, not the current page or filter.
  const [totalAll, setTotalAll] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [tab, setTab] = useState<ListTab>('all');
  // Switching mode inverts what every mark means, so with marks present the switch
  // goes through this pending state, a dialog, and a CLEAR — never a silent flip.
  const [pendingMode, setPendingMode] = useState<SourceFilterMode | null>(null);
  // Creating a device is its own step with its own fields: the backend needs a
  // provider and an id, and the id is only unique within that provider.
  const [showCreate, setShowCreate] = useState(false);
  // The row a just-created device landed on: briefly tinted and scrolled into
  // view, so the user sees where it sits in the active list.
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  // The token refreshes on a timer; reading it through a ref keeps a refresh from
  // re-firing the load effect and snapping the list back to page one mid-review.
  const tokenRef = useRef(token);
  tokenRef.current = token;
  // Marks stale responses: only the latest request may write the list. The debounce
  // delays the START of a request, not its landing order.
  const requestSeq = useRef(0);
  // Where the SERVER's pages end. devices.length can't say it: manual creates append
  // locally and would skew the next offset, silently skipping a catalogue row.
  const serverOffsetRef = useRef(0);

  const blocking = value.mode === 'blacklist';
  const markedIds = useMemo(
    () => new Set(value.sources.map((source) => source.id)),
    [value.sources],
  );

  const loadDevices = useCallback(
    async (term: string, offset: number) => {
      const currentToken = tokenRef.current;
      if (!currentToken || typeof currentToken !== 'string' || !providerId)
        return;
      const seq = ++requestSeq.current;
      setLoading(true);
      try {
        const query: Record<string, string | number> = { offset };
        if (term) query.search = term;
        const data = await getSourcesForRoute(currentToken, providerId, query);
        // The provider-wide total holds whichever request it came from: a search
        // started before the first load landed must not leave every count at zero.
        if (!term) setTotalAll(data?.count ?? 0);
        if (seq !== requestSeq.current) return; // a newer request superseded this one
        const results: SourceSummary[] = data?.results ?? [];
        setLoadFailed(false);
        setTotal(data?.count ?? 0);
        serverOffsetRef.current = offset + results.length;
        // De-duped: a manually created device is appended locally, so the server
        // page that later covers its offset re-delivers it.
        setDevices((prev) => {
          if (offset === 0) return results;
          const seen = new Set(prev.map((device) => device.id));
          return [...prev, ...results.filter((device) => !seen.has(device.id))];
        });
      } catch {
        // Without this, a 5xx is an unhandled rejection and the editor renders the
        // misleading "this provider has no devices" empty state.
        if (seq === requestSeq.current) setLoadFailed(true);
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    },
    [providerId],
  );

  // The first, unfiltered load runs as soon as a token exists — it is what fills
  // totalAll (the provider-wide counts), and the token can arrive after mount. It is
  // deliberately not cancellable by typing: killing it left every count at zero.
  const initialLoadDoneRef = useRef(false);
  useEffect(() => {
    if (initialLoadDoneRef.current) return;
    if (!token || typeof token !== 'string') return;
    initialLoadDoneRef.current = true;
    loadDevices('', 0);
  }, [token, loadDevices]);

  // Typed searches debounce; each new term resets to offset 0.
  const searchDirtyRef = useRef(false);
  useEffect(() => {
    if (!searchDirtyRef.current) {
      // Skip the mount run — the kickoff effect owns the first load.
      searchDirtyRef.current = true;
      return;
    }
    const timer = setTimeout(
      () => loadDevices(search.trim(), 0),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [search, loadDevices]);

  const mark = (source: SourceSummary) => {
    if (markedIds.has(source.id)) return;
    onChange({ ...value, sources: [...value.sources, source] });
  };
  const unmark = (id: string) =>
    onChange({
      ...value,
      sources: value.sources.filter((item) => item.id !== id),
    });

  const requestMode = (mode: SourceFilterMode) => {
    if (mode === value.mode) return;
    if (value.sources.length === 0) {
      onChange({ ...value, mode });
      return;
    }
    setPendingMode(mode);
  };

  const openCreate = () => setShowCreate(true);

  // For a device that has not reported yet there is nothing to search for, so it is
  // created here. It arrives pre-marked — creating a device to leave it on the
  // mode's default would have no effect at all.
  const submitCreate = async (payload: {
    external_id: string;
    name?: string;
  }) => {
    const currentToken = tokenRef.current;
    // Rejecting keeps the form open with its error; resolving would close it as
    // if the source had been created.
    if (!currentToken || typeof currentToken !== 'string')
      throw new Error('No session token');
    const created: SourceSummary = await createSource(currentToken, {
      provider: providerId,
      ...payload,
    });
    onChange({ ...value, sources: [...value.sources, created] });
    setDevices((prev) => [...prev, created]);
    setTotal((prev) => prev + 1);
    setTotalAll((prev) => prev + 1);
    // Pre-marked, so the unmarked tab would hide it — land somewhere it shows.
    if (tab === 'unmarked') setTab('all');
    setHighlightId(created.id);
  };

  // Walk the user's eye to the new row: scroll it into view, tint it, let the
  // tint fade (~1s). The row renders marked, so it carries the marked testid.
  useEffect(() => {
    if (!highlightId) return;
    const frame = window.requestAnimationFrame(() => {
      const row = listRef.current?.querySelector<HTMLElement>(
        `[data-highlight-id="${highlightId}"]`,
      );
      row?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    });
    const timer = setTimeout(() => setHighlightId(null), 1000);
    return () => {
      window.cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [highlightId]);

  const markedCount = value.sources.length;
  const unmarkedCount = Math.max(0, totalAll - markedCount);
  const blockedCount = blocking ? markedCount : unmarkedCount;
  const reachCount = blocking ? unmarkedCount : markedCount;

  // The search narrows BOTH sections: the catalogue server-side, the marks here.
  const term = search.trim().toLowerCase();
  const markedVisible = term
    ? value.sources.filter(
        (source) =>
          source.external_id.toLowerCase().includes(term) ||
          (source.name ?? '').toLowerCase().includes(term),
      )
    : value.sources;
  const neutralVisible = devices.filter((source) => !markedIds.has(source.id));

  // Alphabetical by what the row shows first — the name when there is one, the ID
  // otherwise. The key never changes on mark/unmark, so toggling a row never moves
  // it and the user keeps their place; a new device simply lands at its spot.
  const byDisplayName = (a: SourceSummary, b: SourceSummary) =>
    (a.name || a.external_id).localeCompare(
      b.name || b.external_id,
      undefined,
      { sensitivity: 'base' },
    ) ||
    a.external_id.localeCompare(b.external_id) ||
    a.id.localeCompare(b.id);

  // The All tab merges the loaded catalogue pages with the marked devices those
  // pages don't contain (a saved rule can reference devices beyond page one).
  const loadedIds = useMemo(
    () => new Set(devices.map((device) => device.id)),
    [devices],
  );
  const allVisible = [
    ...devices,
    ...markedVisible.filter((source) => !loadedIds.has(source.id)),
  ];
  const visibleRows = (
    tab === 'marked'
      ? [...markedVisible]
      : tab === 'unmarked'
        ? [...neutralVisible]
        : allVisible
  ).sort(byDisplayName);

  return (
    <div
      className="flex min-w-0 flex-col gap-4"
      data-testid="SourceFiltersForm"
    >
      {/* Mode — a segmented control, defaulting to block: the rule only ever
                stores exceptions, and "everything reaches" needs no rule at all. */}
      <div
        className={`${toggleClasses.container} w-fit`}
        role="radiogroup"
        aria-label={t('Filter mode')}
      >
        <button
          type="button"
          role="radio"
          aria-checked={blocking}
          onClick={() => requestMode('blacklist')}
          className={blocking ? segmentActive : segmentInactive}
          data-testid="SourceFiltersForm-mode-block"
        >
          {t('Block specific sources')}
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={!blocking}
          onClick={() => requestMode('whitelist')}
          className={!blocking ? segmentActive : segmentInactive}
          data-testid="SourceFiltersForm-mode-allow"
        >
          {t('Allow only specific sources')}
        </button>
      </div>

      {/* The effective counts, right under the mode switch — NOT in the panel
                footer, where they would make this rule's footer differ from every
                other rule's and fight Delete Rule for attention. */}
      {(totalAll > 0 || markedCount > 0) && (
        <span
          className="text-xs font-semibold"
          data-testid="SourceFiltersForm-counters"
        >
          <span className="text-red-2">
            {t('{{count}} blocked', { count: blockedCount })}
          </span>
          <span className="text-ui-text-secondary-text"> · </span>
          <span className="text-green-1">
            {t('{{count}} reaching', { count: reachCount })}
          </span>
        </span>
      )}

      {/* The outcome in plain words, ABOVE the list. This is the answer to
                "what does this rule do", stated once, instead of a chip per row. */}
      {blocking && markedCount === 0 && (
        <div
          className="break-words rounded border border-unlisted-gray-lines bg-greyscale-11 px-3 py-2 text-sm text-ui-text-primary-text"
          data-testid="SourceFiltersForm-summary"
        >
          {t(
            'No sources are blocked. Every source, including new ones, reaches {{name}}.',
            { name: destinationName },
          )}
        </div>
      )}
      {blocking && markedCount > 0 && (
        <div
          className="break-words rounded border border-unlisted-gray-lines bg-greyscale-11 px-3 py-2 text-sm text-ui-text-primary-text"
          data-testid="SourceFiltersForm-summary"
        >
          {t('{{count}} sources are blocked.', { count: markedCount })}{' '}
          {t(
            'All other sources ({{num}} today, plus any new ones) keep reaching {{name}}.',
            { num: unmarkedCount, name: destinationName },
          )}
        </div>
      )}
      {!blocking && markedCount > 0 && (
        <div
          className="break-words rounded border border-unlisted-gray-lines bg-greyscale-11 px-3 py-2 text-sm text-ui-text-primary-text"
          data-testid="SourceFiltersForm-summary"
        >
          {t('Only {{count}} sources reach {{name}}.', {
            count: markedCount,
            name: destinationName,
          })}{' '}
          {t('Everything else, including new sources, is blocked.')}
        </div>
      )}
      {!blocking && markedCount === 0 && (
        // Neutral, not alarming: Save is disabled until something is allowed, so
        // the empty state can never ship — the box instructs instead of warning.
        <div
          className="break-words rounded border border-unlisted-gray-lines bg-greyscale-11 px-3 py-2 text-sm text-ui-text-primary-text"
          data-testid="SourceFiltersForm-summary"
        >
          {t(
            'Mark the sources that should reach {{name}} — the rule needs at least one allowed source before it can be saved.',
            { name: destinationName },
          )}
        </div>
      )}
      <input
        id="source-filter-search"
        className={formClasses.input}
        type="search"
        autoComplete="off"
        placeholder={t('Search by source ID or name')}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        data-testid="SourceFiltersForm-search"
      />

      {/* Tabs, not a checkbox: each names a set and carries its count. The add
                button shares the table's header row, same style as the Sources tab's. */}
      <div className="flex items-center gap-1" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'all'}
          onClick={() => setTab('all')}
          className={`${tabBase} ${tab === 'all' ? 'bg-ui-cta-primary text-white' : 'bg-greyscale-11 text-ui-text-secondary-text hover:text-ui-text-primary-text'}`}
          data-testid="SourceFiltersForm-tab-all"
        >
          {t('All')} ({totalAll})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'marked'}
          onClick={() => setTab('marked')}
          className={`${tabBase} ${tab === 'marked' ? 'bg-ui-cta-primary text-white' : 'bg-greyscale-11 text-ui-text-secondary-text hover:text-ui-text-primary-text'}`}
          data-testid="SourceFiltersForm-tab-marked"
        >
          {blocking ? t('Blocked') : t('Allowed')} ({markedCount})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'unmarked'}
          onClick={() => setTab('unmarked')}
          className={`${tabBase} ${tab === 'unmarked' ? 'bg-ui-cta-primary text-white' : 'bg-greyscale-11 text-ui-text-secondary-text hover:text-ui-text-primary-text'}`}
          data-testid="SourceFiltersForm-tab-unmarked"
        >
          {blocking ? t('Reaching') : t('Not reaching')} ({unmarkedCount})
        </button>
        <button
          type="button"
          onClick={openCreate}
          className={`ml-auto whitespace-nowrap ${buttonClasses.primarySm}`}
          data-testid="SourceFiltersForm-open-create"
        >
          + {t('Add source')}
        </button>
      </div>

      <div
        ref={listRef}
        className="max-h-80 overflow-auto rounded border border-unlisted-gray-lines scrollbar-thin"
        data-testid="SourceFiltersForm-list"
      >
        {loadFailed ? (
          <div
            className="px-3 py-3 text-sm text-ui-status-error-destructive"
            data-testid="SourceFiltersForm-load-error"
          >
            {t('Something went wrong. Please try again or contact support.')}
          </div>
        ) : loading && visibleRows.length === 0 ? (
          <div className="px-3 py-3 text-sm text-ui-text-secondary-text">
            {t('Loading sources…')}
          </div>
        ) : (
          <ul className="m-0 list-none divide-y divide-unlisted-gray-lines p-0">
            {visibleRows.map((source) => (
              <DeviceRow
                key={source.id}
                source={source}
                marked={markedIds.has(source.id)}
                blocking={blocking}
                highlighted={source.id === highlightId}
                onMark={mark}
                onUnmark={unmark}
              />
            ))}
            {/* "No devices" only when there truly are none left to page in —
                            a fully-marked first page must invite Load more, not lie. */}
            {visibleRows.length === 0 &&
              (tab === 'marked' || serverOffsetRef.current >= total) && (
                <li
                  className="px-3 py-3 text-sm text-ui-text-secondary-text"
                  data-testid="SourceFiltersForm-no-devices"
                >
                  {tab === 'marked'
                    ? blocking
                      ? t('No sources are blocked yet.')
                      : t('No sources are allowed yet.')
                    : search.trim()
                      ? t('No source matches "{{term}}".', {
                          term: search.trim(),
                        })
                      : t('This provider has no sources yet.')}
                </li>
              )}
            {/* The catalogue-backed tabs page in more; the marked tab already
                            holds the whole rule. A fully-marked first page must still be
                            able to reach the rest, so this renders even over an empty list. */}
            {tab !== 'marked' && serverOffsetRef.current < total && (
              <li className="px-3 py-2">
                <button
                  type="button"
                  onClick={() =>
                    loadDevices(search.trim(), serverOffsetRef.current)
                  }
                  disabled={loading}
                  className={buttonClasses.inlineLink}
                  data-testid="SourceFiltersForm-load-more"
                >
                  {loading ? t('Loading sources…') : t('Load more')}
                </button>
              </li>
            )}
          </ul>
        )}
      </div>

      <AddDeviceModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreate={submitCreate}
        initialExternalId={search.trim()}
        testIdPrefix="SourceFiltersForm"
      />

      {pendingMode && (
        <ModalShell
          title={t('Change filter mode?')}
          onClose={() => setPendingMode(null)}
          footer={
            <>
              {/* Switching CLEARS the unsaved marks, so the confirm is styled
                                destructive: it discards work. Both buttons share Body/MD. */}
              <button
                className={buttonClasses.destructiveMd}
                type="button"
                onClick={() => {
                  onChange({ mode: pendingMode, sources: [] });
                  setPendingMode(null);
                }}
                data-testid="SourceFiltersForm-mode-confirm"
              >
                {t('Switch and clear')}
              </button>
              <button
                className={buttonClasses.cancelMd}
                type="button"
                onClick={() => setPendingMode(null)}
                data-testid="SourceFiltersForm-mode-cancel"
              >
                {t('Cancel')}
              </button>
            </>
          }
        >
          <p
            className="break-words text-sm text-ui-text-primary-text"
            data-testid="SourceFiltersForm-mode-dialog-text"
          >
            {pendingMode === 'whitelist'
              ? t(
                  'Switching to allow-only clears your {{count}} blocked sources.',
                  { count: value.sources.length },
                )
              : t(
                  'Switching to block mode clears your {{count}} allowed sources.',
                  { count: value.sources.length },
                )}
          </p>
        </ModalShell>
      )}
    </div>
  );
};

export default SourceFiltersForm;
